import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { campaignConcept, campaignStages, campaignStatement, type CampaignStage } from '../../data/campaign';
interface ConceptOverlayProps {
  open: boolean;
  onClose: () => void;
  onDeepDive: (stage: CampaignStage) => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function ConceptOverlay({
  open,
  onClose,
  onDeepDive
}: ConceptOverlayProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-285" data-mp="20vawhe229kr0">
      {open && <motion.div key="concept" className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-286" data-mp="3q9h6s21b5u97">
          <div className="absolute inset-0 bg-navy-950/40 backdrop-blur-md" onClick={onClose} data-id="element-287" data-mp="c89kn46kp1m5" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="concept-title" initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.5,
        ease
      }} className="relative flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-[36px] bg-white shadow-2xl" data-id="element-288" data-mp="7a816i58j0a5">
            <div className="flex items-start justify-between gap-4 px-8 pt-8 md:px-10" data-id="element-289" data-mp="2bwolznk2bo8">
              <div data-id="element-290" data-mp="3uexm8k2ez6n2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-teal" data-id="element-291" data-mp="3uee71b45ewcq">
                  {campaignConcept.eyebrow}
                </p>
                <h2 id="concept-title" className="mt-2 font-serif text-[34px] leading-[1.02] text-navy-900 md:text-[40px]" data-id="element-292" data-mp="19phyxz2em026">
                  {campaignConcept.title}
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-navy-900/10 text-navy-700 transition hover:bg-mist-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-293" data-mp="bcvxiamvy8on">
                <XIcon className="h-4 w-4" data-id="element-294" data-mp="3jrdy5rahyly8" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-4 md:px-10" data-id="element-295" data-mp="3osjx46ur35g7">
              <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]" data-id="element-296" data-mp="3ssc80q7m9noq">
                <p className="text-[16px] leading-relaxed text-slate-600" data-id="element-297" data-mp="1fhfi6lbdbu8">{campaignConcept.body}</p>
                <div className="rounded-2xl bg-navy-900 p-5 text-white" data-id="element-298" data-mp="313irerday8em">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-pulse-teal" data-id="element-299" data-mp="3542o5ttyra08">
                    {campaignConcept.principle.title}
                  </p>
                  <p className="mt-2 text-[14px] leading-relaxed text-white/80" data-id="element-300" data-mp="2xifn81pzkh8d">{campaignConcept.principle.body}</p>
                </div>
              </div>

              <ul className="mt-6 grid gap-3 md:grid-cols-3" data-id="element-301" data-mp="2oisjh99sazc5">
                {campaignConcept.pillars.map(p => <li key={p.title} className="rounded-2xl border border-navy-900/[0.08] p-4" data-id="element-302" data-mp="2jkr0eoccz00o">
                    <p className="text-[14px] font-semibold text-navy-900" data-id="element-303" data-mp="2enijss6uz41j">{p.title}</p>
                    <p className="mt-1 text-[13px] leading-snug text-slate-500" data-id="element-304" data-mp="29ufmc8yynag2">{p.body}</p>
                  </li>)}
              </ul>

              <h3 className="mt-8 text-[12px] font-semibold uppercase tracking-[0.16em] text-navy-900" data-id="element-305" data-mp="20uizmcw4slnn">
                Deep dive into the three use cases
              </h3>
              <ol className="mt-3 grid gap-3 md:grid-cols-3" data-id="element-306" data-mp="1vxiuc473vmxl">
                {campaignStages.map((stage, i) => <motion.li key={stage.id} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.15 + i * 0.08,
              ease
            }} data-id="element-307" data-mp="3qymblu0ds270" data-mpi={i}>
                    <button type="button" onClick={() => onDeepDive(stage)} className="group flex h-full w-full flex-col rounded-2xl p-5 text-left transition hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" style={{
                backgroundColor: `${stage.accent}10`
              }} data-id="element-308" data-mp="3h8jaf6j3vhxl">
                      <span className="font-serif text-[28px] leading-none" style={{
                  color: stage.accent
                }} data-id="element-309" data-mp="2yc1mz1bai475">
                        {stage.number}
                      </span>
                      <span className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{
                  color: stage.accent
                }} data-id="element-310" data-mp="wj3nwnqx6fdr">
                        {stage.moment}
                      </span>
                      <span className="mt-1 text-[18px] font-semibold text-navy-900" data-id="element-311" data-mp="2m07k67nshuf3">{stage.name}</span>
                      <span className="text-[13px] text-slate-500" data-id="element-312" data-mp="2h263tuhr097b">{stage.verbs}</span>
                      <span className="mt-3 flex-1 text-[14px] leading-snug text-navy-800" data-id="element-313" data-mp="2vqamiygswkq3">{stage.tagline}</span>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy-900" data-id="element-314" data-mp="2qx7ooe4zqp43">
                        Deep dive
                        <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" style={{
                    color: stage.accent
                  }} data-id="element-315" data-mp="2zrm5wm9309pu" />
                      </span>
                    </button>
                  </motion.li>)}
              </ol>

              <div className="mt-8 border-t border-navy-900/[0.06] pt-6 text-center" data-id="element-316" data-mp="1kb58iyb7yzr6">
                <p className="font-serif text-[22px] leading-snug text-navy-900 md:text-[26px]" data-id="element-317" data-mp="1fcara0swhj96">{campaignStatement.main}</p>
                <p className="mt-2 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-500" data-id="element-318" data-mp="250zdtjz5i3m7">
                  {campaignStatement.sub}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}